import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  inject,
  OnDestroy,
  OnInit,
  signal,
  viewChild,
  WritableSignal,
} from '@angular/core';
import {
  FormControl,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { MatMenuModule } from '@angular/material/menu';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { allCountries, Country } from 'country-telephone-data';
import { catchError, concatMap, EMPTY, forkJoin } from 'rxjs';

import { UserInfo } from '@blaze-shared/api-contracts-domain';
import {
  AuthenticatorCode,
  TwoFactorAuthApiService,
  UsersApiService,
} from '@blaze-shared/auth-data-access';
import { NotificationService } from '@blaze-shared/platform-data-access';
import {
  ButtonComponent,
  DialogComponent,
  ElementLoadingIndicatorComponent,
  PasswordVisibilityToggleDirective,
} from '@blaze-shared/shared-ui';

interface CodeFormGroup {
  code: FormControl<string>;
}
interface PasswordFormGroup {
  password: FormControl<string>;
}
interface PhoneFormGroup {
  phone: FormControl<string | null>;
}

export interface TwoFactorAuthSetupDialogComponentInput {
  disableTwoFactorAuth: boolean;
  user: UserInfo;
}
export interface TwoFactorAuthSetupDialogComponentOutput {
  user: UserInfo;
  wasTwoFactorAuthDisabled: boolean;
}

@Component({
  selector: 'blaze-two-factor-auth-setup-dialog',
  imports: [
    ButtonComponent,
    DialogComponent,
    ElementLoadingIndicatorComponent,
    FormsModule,
    MatIconModule,
    MatMenuModule,
    MatSlideToggleModule,
    PasswordVisibilityToggleDirective,
    ReactiveFormsModule,
  ],
  templateUrl: './two-factor-auth-setup-dialog.component.html',
  styleUrls: ['./two-factor-auth-setup-dialog.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class TwoFactorAuthSetupDialogComponent implements OnInit, AfterViewInit, OnDestroy {
  private readonly data = inject<TwoFactorAuthSetupDialogComponentInput>(MAT_DIALOG_DATA);
  private readonly dialogRef = inject(MatDialogRef<TwoFactorAuthSetupDialogComponent>);
  private readonly notificationService = inject(NotificationService);
  private readonly twoFactorAuthApiService = inject(TwoFactorAuthApiService);
  private readonly twoFactorAuthService = inject(TwoFactorAuthApiService);
  private readonly userApiService = inject(UsersApiService);

  dialog = viewChild('dialog', { read: DialogComponent });
  passwordInput = viewChild('passwordInput', { read: ElementRef<HTMLInputElement> });

  protected codeForm: FormGroup<CodeFormGroup>;
  protected passwordForm: FormGroup<PasswordFormGroup>;
  protected phoneForm: FormGroup<PhoneFormGroup>;

  protected readonly authenticatorCode = signal<AuthenticatorCode | null>(null);
  protected readonly authType = signal<'Sms' | 'Authenticator'>('Sms');
  protected readonly changeToken = signal<string>('');
  protected readonly countries: Country[] = allCountries;
  protected readonly country = signal<Country>(
    allCountries.find((country) => country.iso2 === 'us') as Country,
  );
  protected readonly disableTwoFactorAuth = this.data.disableTwoFactorAuth;
  protected readonly emergencyBackupCode = signal('');
  protected readonly enterCodeManually = signal(false);
  protected readonly isApiCallInProgress = signal(false);
  protected readonly isCodeSent = signal(false);
  protected readonly userHasPasswordLogin = signal(false);
  protected readonly userHasTwoFactorAuthEnabled = signal(false);
  protected readonly wasAuthDisabled = signal(false);

  protected user: WritableSignal<UserInfo>;

  constructor() {
    // Overwrite close to return data even when user presses Esc button.
    const originalClose = this.dialogRef.close.bind(this.dialogRef);
    this.dialogRef.close = (): void => {
      const outputData: TwoFactorAuthSetupDialogComponentOutput = {
        user: this.user(),
        wasTwoFactorAuthDisabled: this.wasAuthDisabled(),
      };
      originalClose(outputData);
    };
  }

  ngOnInit(): void {
    this.initForms();
    this.initSignals();
  }

  ngAfterViewInit(): void {
    this.passwordInput()?.nativeElement?.focus();
  }

  ngOnDestroy(): void {
    this.isApiCallInProgress.set(false);
  }

  protected onCloseDialog(): void {
    this.dialogRef.close();
  }

  protected onCopyBackupCode(): void {
    navigator.clipboard.writeText(this.emergencyBackupCode());
    this.notificationService.info('Emergency backup code copied.');
  }

  /**
   * Disables two-factor authentication for the user.
   */
  protected onDisableTwoFactorAuth(): void {
    const password = this.passwordForm.get('password')?.value;
    if (!password) {
      return;
    }
    this.isApiCallInProgress.set(true);
    this.twoFactorAuthService
      .getChangeToken(password)
      .pipe(
        concatMap((changeToken) => this.twoFactorAuthService.disable(changeToken)),
        concatMap(() => this.userApiService.getRefresh()),
        catchError((err) => {
          this.notificationService.showError(err);
          this.isApiCallInProgress.set(false);
          return EMPTY;
        }),
      )
      .subscribe((loginInfo) => {
        const updatedUser = loginInfo?.user;
        if (!updatedUser) {
          console.error('Unable to disable two-factor authentication.');
          return;
        }
        this.wasAuthDisabled.set(!updatedUser.hasTwoFactorAuthEnabled);
        this.user.set(updatedUser);
        this.onCloseDialog();
      });
  }

  /**
   * Using entered user password, fetches a token which is used to set up two-factor authentication.
   */
  protected onGetChangeToken(): void {
    const password = this.passwordForm.get('password')?.value;
    if (!password) {
      return;
    }
    this.twoFactorAuthApiService
      .getChangeToken(password)
      .pipe(
        catchError((err) => {
          this.notificationService.showError(err);
          return EMPTY;
        }),
      )
      .subscribe((changeToken) => {
        this.changeToken.set(changeToken);
      });
  }

  protected onSendCodeToMobile(): void {
    if (!this.changeToken()) {
      return;
    }
    const phoneNumber = `+${this.country().dialCode}${this.phoneForm.get('phone')?.value}`;
    this.twoFactorAuthApiService
      .enableSmsAuth(this.changeToken(), phoneNumber)
      .pipe(
        catchError((err) => {
          this.notificationService.showError(err);
          return EMPTY;
        }),
      )
      .subscribe(() => {
        this.isCodeSent.set(true);
      });
  }

  protected onSetCountry(country: Country): void {
    this.country.set(country);
  }

  protected onSetAuthType(authType: 'Sms' | 'Authenticator'): void {
    this.authType.set(authType);
    this.phoneForm.reset();
    this.codeForm.reset();
    this.authenticatorCode.set(null);
    this.enterCodeManually.set(false);
    this.isCodeSent.set(false);

    if (authType === 'Authenticator' && this.changeToken()) {
      this.twoFactorAuthApiService
        .enableAuthenticatorAuth(this.changeToken())
        .pipe(
          catchError((err) => {
            this.notificationService.showError(err);
            return EMPTY;
          }),
        )
        .subscribe((code) => {
          if (code) {
            this.authenticatorCode.set(code);
          }
        });
    }
  }

  protected onToggleEnterCodeManually(): void {
    this.enterCodeManually.set(!this.enterCodeManually());
  }

  protected onVerifyAuthCode(): void {
    if (!this.changeToken()) {
      return;
    }
    this.twoFactorAuthApiService
      .verifyCode(this.changeToken(), this.codeForm.get('code')?.value || '')
      .pipe(
        concatMap(() =>
          forkJoin([
            this.userApiService.getRefresh(),
            this.twoFactorAuthApiService.createEmergencyCode(this.changeToken() || ''),
          ]),
        ),
        catchError((err) => {
          this.notificationService.showError(err);
          return EMPTY;
        }),
      )
      .subscribe(([loginInfo, config]) => {
        if (!loginInfo?.user || !config?.emergencyBackupCode) {
          return;
        }
        this.userHasTwoFactorAuthEnabled.set(true);
        this.user.set(loginInfo.user);
        this.emergencyBackupCode.set(config.emergencyBackupCode);
      });
  }

  private initSignals(): void {
    this.user = signal(this.data.user);
    this.userHasTwoFactorAuthEnabled.set(!!this.user().hasTwoFactorAuthEnabled);
    this.userHasPasswordLogin.set(!!this.user().loginTypes?.includes('Password'));
  }

  private initForms(): void {
    this.codeForm = new FormGroup<CodeFormGroup>({
      code: new FormControl<string>('', { nonNullable: true, validators: [Validators.required] }),
    });

    this.passwordForm = new FormGroup<PasswordFormGroup>({
      password: new FormControl<string>('', {
        nonNullable: true,
        validators: [Validators.required],
      }),
    });

    this.phoneForm = new FormGroup<PhoneFormGroup>({
      phone: new FormControl<string>('', { validators: [Validators.required] }),
    });
  }
}
